import { useEffect, useState, type FormEvent } from 'react';

import { ApiError, createUser } from '../lib/api';
import type { Role, UserResponse } from '../lib/types';
import { SparkInput } from './SparkInput';
import { SparkMessage } from './SparkMessage';

interface CreateUserDialogProps {
  /** Roles the signed-in administrator is allowed to hand out. */
  assignableRoles: Role[];
  defaultPcc: string;
  /** Only root may grant ticketing permission. */
  canGrantTicketing: boolean;
  onClose: () => void;
  onCreated: (result: UserResponse) => void;
}

/** The root/admin form for provisioning a new agency account. */
export function CreateUserDialog({
  assignableRoles,
  defaultPcc,
  canGrantTicketing,
  onClose,
  onCreated,
}: CreateUserDialogProps) {
  const [userId, setUserId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [pcc, setPcc] = useState(defaultPcc);
  const [role, setRole] = useState<Role>(assignableRoles[0] ?? 'agent');
  const [password, setPassword] = useState('');
  const [generate, setGenerate] = useState(true);
  const [mustChange, setMustChange] = useState(true);
  const [ticketing, setTicketing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');

    const errors: Record<string, string> = {};
    if (!userId.trim()) errors.userId = 'Username is required.';
    if (!generate && !password) errors.password = 'Enter a password or let the system generate one.';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      const result = await createUser({
        userId: userId.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        pcc: pcc.trim(),
        role,
        password: generate ? undefined : password,
        mustChangePassword: mustChange,
        ticketing: canGrantTicketing ? ticketing : undefined,
      });
      onCreated(result);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormError(error.message);
        setFieldErrors(error.fields ?? {});
      } else {
        setFormError('The user could not be created. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="create-user-title">
        <div className="dialog__header">
          <h2 className="dialog__title" id="create-user-title">
            Create user
          </h2>
          <button type="button" className="dialog__close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="dialog__body">
            {formError && <SparkMessage variant="error">{formError}</SparkMessage>}

            <div className="form-grid">
              <div className="form-grid--full">
                <SparkInput
                  label="Username"
                  placeholder="Example: 4321"
                  maxLength={20}
                  autoFocus
                  value={userId}
                  error={fieldErrors.userId}
                  onChange={(event) => setUserId(event.target.value)}
                  disabled={submitting}
                />
              </div>

              <SparkInput
                label="First name"
                value={firstName}
                error={fieldErrors.firstName}
                onChange={(event) => setFirstName(event.target.value)}
                disabled={submitting}
              />
              <SparkInput
                label="Last name"
                value={lastName}
                error={fieldErrors.lastName}
                onChange={(event) => setLastName(event.target.value)}
                disabled={submitting}
              />

              <div className="form-grid--full">
                <SparkInput
                  label="Email"
                  type="email"
                  placeholder="agent@agency.com"
                  value={email}
                  error={fieldErrors.email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={submitting}
                />
              </div>

              <SparkInput
                label="PCC"
                placeholder="Example: BTD0"
                maxLength={10}
                value={pcc}
                error={fieldErrors.pcc}
                onChange={(event) => setPcc(event.target.value.toUpperCase())}
                disabled={submitting}
              />

              <label className="spark-input" htmlFor="new-user-role">
                <span className="spark-label">Role</span>
                <select
                  id="new-user-role"
                  className="spark-input__field"
                  value={role}
                  onChange={(event) => setRole(event.target.value as Role)}
                  disabled={submitting || assignableRoles.length === 1}
                >
                  {assignableRoles.map((value) => (
                    <option key={value} value={value}>
                      {value === 'admin' ? 'Administrator' : 'Agent'}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="checkbox">
              <input
                type="checkbox"
                checked={generate}
                onChange={(event) => {
                  setGenerate(event.target.checked);
                  if (event.target.checked) setPassword('');
                }}
                disabled={submitting}
              />
              Generate a temporary password
            </label>

            {!generate && (
              <SparkInput
                label="Password"
                type="password"
                autoComplete="new-password"
                value={password}
                error={fieldErrors.password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={submitting}
              />
            )}

            <label className="checkbox">
              <input
                type="checkbox"
                checked={mustChange}
                onChange={(event) => setMustChange(event.target.checked)}
                disabled={submitting}
              />
              Require a password change at first sign-in
            </label>

            {canGrantTicketing && (
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={ticketing}
                  onChange={(event) => setTicketing(event.target.checked)}
                  disabled={submitting}
                />
                Allowed to issue tickets (otherwise W* answers NOTKTS)
              </label>
            )}
          </div>

          <div className="dialog__footer">
            <button type="button" className="spark-btn spark-btn--md spark-btn--secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="spark-btn spark-btn--md spark-btn--primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create user'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
